<template>
  <v-card flat color="transparent" class="pa-4">
    <v-row align="center" class="mb-3">
      <v-col cols="12" md="8">
        <div class="d-flex align-center" style="gap: 6px">
          <h2 class="font-weight-bold">Plan de producción</h2>
          <produccion-ayuda />
        </div>
        <p class="grey--text mb-0">
          Pedidos pendientes ordenados por prioridad. Ajustá las placas y generá las órdenes.
        </p>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-btn outlined color="grey" :loading="loading" @click="fetchPlan">
          <v-icon left>mdi-refresh</v-icon> Recalcular
        </v-btn>
      </v-col>
    </v-row>

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="deep-purple" />
    </div>

    <template v-else-if="plan">
      <v-tabs v-model="tab" background-color="transparent" color="deep-purple" class="mb-4">
        <v-tab>
          <v-icon left small>mdi-saw-blade</v-icon> Por corte
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-clipboard-account</v-icon> Por pedido
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" class="transparent">

        <!-- ======================== TAB 0: POR CORTE ======================== -->
        <v-tab-item>
          <!-- Pedidos pendientes -->
          <div class="subtitle-2 font-weight-bold mb-2 mt-1">
            Pedidos pendientes
            <span class="grey--text font-weight-regular caption ml-1">(en orden de prioridad)</span>
          </div>

          <v-alert v-if="!plan.pedidos_pendientes || plan.pedidos_pendientes.length === 0"
            type="success" text dense class="mb-4">
            No hay pedidos pendientes con faltantes de stock.
          </v-alert>

          <v-card v-else outlined class="mb-5">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th style="width:90px">Prioridad</th>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in plan.pedidos_pendientes" :key="p.id">
                  <td>
                    <div class="d-flex align-center" style="gap:4px">
                      <v-text-field
                        :value="p.prioridad || ''"
                        type="number"
                        min="0"
                        dense
                        hide-details
                        outlined
                        style="width:60px"
                        class="caption"
                        placeholder="—"
                        @change="setPrioridad(p, $event)"
                      />
                    </div>
                  </td>
                  <td>
                    <span class="caption font-weight-medium">{{ p.numero_display }}</span>
                  </td>
                  <td class="caption">{{ p.cliente }}</td>
                  <td>
                    <v-chip x-small :color="estadoColor(p.estado)">{{ p.estado }}</v-chip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <div class="caption grey--text pa-2 pl-3">
              Prioridad 1 = más urgente. Dejá en blanco para sin prioridad (va al final).
            </div>
          </v-card>

          <!-- Advertencias -->
          <v-alert
            v-for="(adv, i) in plan.advertencias" :key="'adv' + i"
            type="warning" text dense class="mb-2"
          >{{ adv }}</v-alert>

          <!-- Cortes a realizar -->
          <template v-if="lineasAProducir.length > 0">
            <div class="subtitle-2 font-weight-bold mb-2">
              Cortes a realizar
              <span class="grey--text font-weight-regular caption ml-1">
                — editá las placas antes de generar
              </span>
            </div>

            <v-card outlined class="mb-1">
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Programa</th>
                    <th class="text-right" style="width:90px">
                      <v-tooltip bottom max-width="220">
                        <template v-slot:activator="{ on }">
                          <span v-on="on" style="cursor:help; border-bottom:1px dotted">Necesitás</span>
                        </template>
                        <span>Unidades que faltan en stock para cubrir los pedidos pendientes</span>
                      </v-tooltip>
                    </th>
                    <th class="text-right" style="width:90px">
                      <v-tooltip bottom max-width="240">
                        <template v-slot:activator="{ on }">
                          <span v-on="on" style="cursor:help; border-bottom:1px dotted">En cola</span>
                        </template>
                        <span>Unidades ya programadas en órdenes pendientes o en proceso — se descuentan del cálculo.</span>
                      </v-tooltip>
                    </th>
                    <th class="text-right" style="width:90px">
                      <v-tooltip bottom max-width="220">
                        <template v-slot:activator="{ on }">
                          <span v-on="on" style="cursor:help; border-bottom:1px dotted">Rinde</span>
                        </template>
                        <span>Cuántas unidades produce cada placa de este programa</span>
                      </v-tooltip>
                    </th>
                    <th class="text-center" style="width:110px">
                      <v-tooltip bottom max-width="220">
                        <template v-slot:activator="{ on }">
                          <span v-on="on" style="cursor:help; border-bottom:1px dotted">Placas</span>
                        </template>
                        <span>Cantidad de placas a cortar. El sistema calcula el mínimo necesario — podés ajustarlo.</span>
                      </v-tooltip>
                    </th>
                    <th class="text-right" style="width:90px">
                      <v-tooltip bottom max-width="240">
                        <template v-slot:activator="{ on }">
                          <span v-on="on" style="cursor:help; border-bottom:1px dotted">Produce</span>
                        </template>
                        <span>Unidades que se van a producir con las placas indicadas. Puede ser un poco más que "Necesitás" porque las placas son enteras (no se puede cortar media placa).</span>
                      </v-tooltip>
                    </th>
                    <th>Pedidos que cubre</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(l, i) in lineasAProducir" :key="i">
                    <td>
                      <span class="caption">{{ l.receta_nombre }}</span>
                      <v-chip v-if="!l.receta_nombre" x-small color="red" dark>sin receta</v-chip>
                      <div v-if="l.salidas && l.salidas.length" class="grey--text" style="font-size:10px; line-height:1.4">
                        Corta:
                        <template v-for="(s, si) in l.salidas">
                          <span :key="si">
                            {{ s.descripcion }} ×{{ n(s.cantidad_por_placa) }}<span v-if="!s.en_demanda" class="amber--text text--darken-2"> (de paso)</span><span v-if="si < l.salidas.length - 1"> · </span>
                          </span>
                        </template>
                      </div>
                    </td>
                    <td class="text-right orange--text font-weight-bold caption">{{ l.faltante }}</td>
                    <td class="text-right caption">
                      <span v-if="l.en_cola > 0" class="teal--text font-weight-medium">{{ l.en_cola }}</span>
                      <span v-else class="grey--text">—</span>
                    </td>
                    <td class="text-right grey--text caption">{{ l.rinde_por_placa }}/placa</td>
                    <td class="text-center">
                      <v-text-field
                        v-model.number="editedLotes[l.receta]"
                        type="number"
                        min="1"
                        dense
                        hide-details
                        outlined
                        style="width:80px; margin: 4px auto"
                        class="caption text-center"
                      />
                    </td>
                    <td class="text-right caption">
                      <span class="green--text font-weight-medium">{{ producePara(l, editedLotes) }}</span>
                      <div
                        v-if="producePara(l, editedLotes) > l.faltante"
                        class="grey--text"
                        style="font-size:10px; line-height:1.2"
                      >+{{ producePara(l, editedLotes) - l.faltante }} extra</div>
                    </td>
                    <td>
                      <div class="d-flex flex-wrap align-center py-1" style="gap:4px">
                        <v-chip
                          v-for="ped in pedidosDeLinea(l)" :key="ped.id"
                          x-small outlined
                          :color="ped.prioridad > 0 ? 'deep-purple' : 'grey'"
                        >
                          <v-icon v-if="ped.prioridad > 0" x-small left>mdi-star</v-icon>
                          {{ ped.cliente }} ({{ n(ped.cantidad) }})
                        </v-chip>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div class="caption grey--text pa-2 pl-3">
                <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                Las placas se redondean al entero siguiente — por eso "Produce" puede ser un poco más que "Necesitás". El excedente queda en stock.
              </div>
            </v-card>

            <!-- Stock resultante -->
            <div class="subtitle-2 font-weight-bold mb-2 mt-4">
              Stock resultante
              <span class="grey--text font-weight-regular caption ml-1">
                — cómo queda cada bandeja (incluye las que salen de paso)
              </span>
            </div>
            <v-card outlined class="mb-4">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Bandeja</th>
                    <th class="text-right" style="width:100px">Stock actual</th>
                    <th class="text-right" style="width:100px">Se produce</th>
                    <th class="text-right" style="width:100px">Stock final</th>
                    <th style="width:80px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in stockResultante(lineasAProducir, editedLotes)" :key="r.psp">
                    <td class="caption">{{ r.descripcion }}</td>
                    <td class="text-right caption grey--text">{{ n(r.stock_actual) }}</td>
                    <td class="text-right caption green--text font-weight-medium">+{{ n(r.produce) }}</td>
                    <td class="text-right caption font-weight-bold">{{ n(r.stock_final) }}</td>
                    <td>
                      <v-chip x-small outlined :color="r.en_demanda ? 'deep-purple' : 'blue-grey'">
                        {{ r.en_demanda ? 'pedido' : 'extra' }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>

            <!-- Totales -->
            <v-row class="mb-4">
              <v-col cols="6" md="3">
                <v-card outlined class="pa-3 text-center">
                  <div class="caption grey--text">Total placas</div>
                  <div class="text-h6 font-weight-bold">{{ totalPlacas }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card outlined class="pa-3 text-center">
                  <div class="caption grey--text">Tiempo estimado</div>
                  <div class="text-h6 font-weight-bold">{{ horas(plan.totales.tiempo_minutos) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card outlined class="pa-3 text-center">
                  <div class="caption grey--text">Costo estimado</div>
                  <div class="text-h6 font-weight-bold">{{ money(plan.totales.costo_estimado) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3" class="d-flex align-center justify-center">
                <v-btn
                  color="deep-purple" dark large
                  :loading="generando"
                  @click="generar"
                >
                  <v-icon left>mdi-playlist-plus</v-icon>
                  Generar órdenes
                </v-btn>
              </v-col>
            </v-row>
          </template>

          <template v-else>
            <!-- Ya hay órdenes de corte generadas en la cola -->
            <v-alert v-if="enColaConsolidado > 0" type="info" text prominent icon="mdi-playlist-check">
              <div class="font-weight-bold">Los pedidos pendientes ya tienen sus cortes generados.</div>
              <div class="body-2 mt-1">
                Hay órdenes de producción en la cola que cubren la demanda
                ({{ n(enColaConsolidado) }} u en cola). No hace falta generar nuevas:
                andá a la <strong>Cola de producción</strong> para iniciarlas y cortar.
                Recién cuando las confirmes se suma el stock de bandejas.
              </div>
              <v-btn small color="info" class="mt-3" @click="irACola">
                <v-icon left small>mdi-playlist-play</v-icon> Ir a la cola de producción
              </v-btn>
            </v-alert>
            <!-- Cubierto por stock físico real -->
            <v-alert v-else type="success" text dense>
              El stock actual cubre todos los pedidos pendientes. No hace falta producir.
            </v-alert>
          </template>
        </v-tab-item>

        <!-- ======================== TAB 1: POR PEDIDO ======================== -->
        <v-tab-item>

          <!-- Sin orden seleccionada: lista de pedidos -->
          <template v-if="!ordenSeleccionada">
            <div class="subtitle-2 font-weight-bold mb-3">
              Elegí un pedido para planificar sus cortes
            </div>

            <v-alert v-if="!plan.pedidos_pendientes || plan.pedidos_pendientes.length === 0"
              type="success" text dense>
              No hay pedidos pendientes con faltantes de stock.
            </v-alert>

            <v-card v-else outlined>
              <v-list dense>
                <v-list-item
                  v-for="p in plan.pedidos_pendientes" :key="p.id"
                  class="py-1"
                  style="min-height:56px; border-bottom: 1px solid rgba(128,128,128,0.15)"
                >
                  <v-list-item-content>
                    <div class="d-flex align-center flex-wrap" style="gap: 8px">
                      <v-chip
                        x-small
                        :color="p.prioridad > 0 ? 'deep-purple' : 'grey lighten-1'"
                        :dark="p.prioridad > 0"
                        class="flex-shrink-0"
                      >
                        {{ p.prioridad > 0 ? '#' + p.prioridad : '—' }}
                      </v-chip>
                      <span class="caption font-weight-medium">{{ p.numero_display }}</span>
                      <span class="caption grey--text">{{ p.cliente }}</span>
                      <v-chip x-small :color="estadoColor(p.estado)">{{ p.estado }}</v-chip>
                    </div>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      small outlined color="deep-purple"
                      :loading="loadingOrden === p.id"
                      @click="seleccionarOrden(p)"
                    >
                      <v-icon left small>mdi-clipboard-list</v-icon>
                      Ver plan
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </template>

          <!-- Con orden seleccionada: plan específico -->
          <template v-else>
            <div class="d-flex align-center mb-4" style="gap: 8px">
              <v-btn icon small @click="volverALista">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <div>
                <div class="subtitle-2 font-weight-bold">
                  Planificando para: {{ ordenSeleccionada.numero_display }}
                </div>
                <div class="caption grey--text">
                  {{ ordenSeleccionada.cliente }}
                  <span v-if="ordenSeleccionada.prioridad > 0"> · Prioridad #{{ ordenSeleccionada.prioridad }}</span>
                </div>
              </div>
            </div>

            <div v-if="loadingOrden" class="text-center py-8">
              <v-progress-circular indeterminate color="deep-purple" />
            </div>

            <template v-else-if="planOrden">
              <v-alert
                v-for="(adv, i) in planOrden.advertencias" :key="'advo' + i"
                type="warning" text dense class="mb-2"
              >{{ adv }}</v-alert>

              <template v-if="lineasOrden.length > 0">
                <div class="subtitle-2 font-weight-bold mb-2">
                  Cortes necesarios
                  <span class="grey--text font-weight-regular caption ml-1">
                    — solo para cubrir este pedido
                  </span>
                </div>

                <v-card outlined class="mb-1">
                  <v-simple-table>
                    <thead>
                      <tr>
                        <th>Programa</th>
                        <th class="text-right" style="width:90px">
                          <v-tooltip bottom max-width="220">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" style="cursor:help; border-bottom:1px dotted">Necesitás</span>
                            </template>
                            <span>Unidades que faltan en stock para cubrir este pedido</span>
                          </v-tooltip>
                        </th>
                        <th class="text-right" style="width:90px">
                          <v-tooltip bottom max-width="220">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" style="cursor:help; border-bottom:1px dotted">Rinde</span>
                            </template>
                            <span>Cuántas unidades produce cada placa de este programa</span>
                          </v-tooltip>
                        </th>
                        <th class="text-center" style="width:110px">
                          <v-tooltip bottom max-width="220">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" style="cursor:help; border-bottom:1px dotted">Placas</span>
                            </template>
                            <span>Cantidad de placas a cortar. Podés ajustarlo si querés producir más o menos.</span>
                          </v-tooltip>
                        </th>
                        <th class="text-right" style="width:90px">
                          <v-tooltip bottom max-width="240">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" style="cursor:help; border-bottom:1px dotted">En cola</span>
                            </template>
                            <span>Unidades ya programadas en órdenes pendientes o en proceso — se descuentan del cálculo.</span>
                          </v-tooltip>
                        </th>
                        <th class="text-right" style="width:90px">
                          <v-tooltip bottom max-width="240">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" style="cursor:help; border-bottom:1px dotted">Produce</span>
                            </template>
                            <span>Unidades que se van a producir. Puede ser un poco más que "Necesitás" porque las placas son enteras.</span>
                          </v-tooltip>
                        </th>
                        <th style="width:110px"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(l, i) in lineasOrden" :key="i">
                        <td>
                          <span class="caption">{{ l.receta_nombre }}</span>
                          <v-chip v-if="!l.receta_nombre" x-small color="red" dark>sin receta</v-chip>
                          <div v-if="l.salidas && l.salidas.length" class="grey--text" style="font-size:10px; line-height:1.4">
                            Corta:
                            <template v-for="(s, si) in l.salidas">
                              <span :key="si">
                                {{ s.descripcion }} ×{{ n(s.cantidad_por_placa) }}<span v-if="!s.en_demanda" class="amber--text text--darken-2"> (de paso)</span><span v-if="si < l.salidas.length - 1"> · </span>
                              </span>
                            </template>
                          </div>
                        </td>
                        <td class="text-right orange--text font-weight-bold caption">{{ l.faltante }}</td>
                        <td class="text-right caption">
                          <span v-if="l.en_cola > 0" class="teal--text font-weight-medium">{{ l.en_cola }}</span>
                          <span v-else class="grey--text">—</span>
                        </td>
                        <td class="text-right grey--text caption">{{ l.rinde_por_placa }}/placa</td>
                        <td class="text-center">
                          <v-text-field
                            v-model.number="editedLotesOrden[l.receta]"
                            type="number"
                            min="1"
                            dense
                            hide-details
                            outlined
                            style="width:80px; margin: 4px auto"
                            class="caption text-center"
                          />
                        </td>
                        <td class="text-right caption">
                          <span class="green--text font-weight-medium">{{ producePara(l, editedLotesOrden) }}</span>
                          <div
                            v-if="producePara(l, editedLotesOrden) > l.faltante"
                            class="grey--text"
                            style="font-size:10px; line-height:1.2"
                          >+{{ producePara(l, editedLotesOrden) - l.faltante }} extra</div>
                        </td>
                        <td class="text-right pr-2">
                          <v-btn
                            x-small outlined color="deep-purple"
                            :loading="generandoLinea === l.receta"
                            :disabled="generandoOrden || generandoLinea !== null"
                            @click="generarLinea(l)"
                          >
                            <v-icon left x-small>mdi-plus</v-icon>
                            Solo este
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <div class="caption grey--text pa-2 pl-3">
                    <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                    Las placas se redondean al entero siguiente. El excedente queda en stock y se descuenta del próximo plan.
                  </div>
                </v-card>

                <!-- Stock resultante -->
                <div class="subtitle-2 font-weight-bold mb-2 mt-4">
                  Stock resultante
                  <span class="grey--text font-weight-regular caption ml-1">
                    — cómo queda cada bandeja (incluye las que salen de paso)
                  </span>
                </div>
                <v-card outlined class="mb-4">
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Bandeja</th>
                        <th class="text-right" style="width:100px">Stock actual</th>
                        <th class="text-right" style="width:100px">Se produce</th>
                        <th class="text-right" style="width:100px">Stock final</th>
                        <th style="width:80px"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="r in stockResultante(lineasOrden, editedLotesOrden)" :key="r.psp">
                        <td class="caption">{{ r.descripcion }}</td>
                        <td class="text-right caption grey--text">{{ n(r.stock_actual) }}</td>
                        <td class="text-right caption green--text font-weight-medium">+{{ n(r.produce) }}</td>
                        <td class="text-right caption font-weight-bold">{{ n(r.stock_final) }}</td>
                        <td>
                          <v-chip x-small outlined :color="r.en_demanda ? 'deep-purple' : 'blue-grey'">
                            {{ r.en_demanda ? 'pedido' : 'extra' }}
                          </v-chip>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>

                <v-row class="mb-4">
                  <v-col cols="6" md="3">
                    <v-card outlined class="pa-3 text-center">
                      <div class="caption grey--text">Total placas</div>
                      <div class="text-h6 font-weight-bold">{{ totalPlacasOrden }}</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-card outlined class="pa-3 text-center">
                      <div class="caption grey--text">Tiempo estimado</div>
                      <div class="text-h6 font-weight-bold">{{ horas(planOrden.totales.tiempo_minutos) }}</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-card outlined class="pa-3 text-center">
                      <div class="caption grey--text">Costo estimado</div>
                      <div class="text-h6 font-weight-bold">{{ money(planOrden.totales.costo_estimado) }}</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="3" class="d-flex align-center justify-center">
                    <v-btn
                      color="deep-purple" dark large
                      :loading="generandoOrden"
                      @click="generarParaOrden"
                    >
                      <v-icon left>mdi-playlist-plus</v-icon>
                      Generar para este pedido
                    </v-btn>
                  </v-col>
                </v-row>
              </template>

              <template v-else>
                <!-- Ya hay órdenes de corte generadas en la cola -->
                <v-alert v-if="enColaOrden > 0" type="info" text prominent icon="mdi-playlist-check">
                  <div class="font-weight-bold">Este pedido ya tiene sus cortes generados.</div>
                  <div class="body-2 mt-1">
                    Hay órdenes de producción en la cola que cubren lo que falta
                    ({{ n(enColaOrden) }} u en cola). No hace falta generar nuevas:
                    andá a la <strong>Cola de producción</strong> para iniciarlas y cortar.
                    Recién cuando las confirmes se suma el stock de bandejas.
                  </div>
                  <v-btn small color="info" class="mt-3" @click="irACola">
                    <v-icon left small>mdi-playlist-play</v-icon> Ir a la cola de producción
                  </v-btn>
                </v-alert>
                <!-- Cubierto por stock físico real -->
                <v-alert v-else type="success" text dense>
                  Tenés stock suficiente para este pedido. No hace falta producir:
                  podés remitirlo directamente.
                </v-alert>
              </template>
            </template>
          </template>

        </v-tab-item>
      </v-tabs-items>
    </template>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import ProduccionAyuda from '../ProduccionAyuda.vue';

export default {
  name: 'PlanProduccion',
  components: { ProduccionAyuda },
  data() {
    return {
      tab: 0,
      plan: null,
      loading: false,
      generando: false,
      editedLotes: {},
      savingPrioridad: {},
      // Por pedido
      ordenSeleccionada: null,
      planOrden: null,
      loadingOrden: false,
      generandoOrden: false,
      generandoLinea: null,
      editedLotesOrden: {},
    };
  },
  computed: {
    lineasAProducir() {
      if (!this.plan) return [];
      return this.plan.lineas.filter(l => l.requiere_produccion && l.receta);
    },
    totalPlacas() {
      return this.lineasAProducir.reduce((sum, l) => sum + (this.editedLotes[l.receta] || l.placas_necesarias), 0);
    },
    lineasOrden() {
      if (!this.planOrden) return [];
      return this.planOrden.lineas.filter(l => l.requiere_produccion && l.receta);
    },
    totalPlacasOrden() {
      return this.lineasOrden.reduce((sum, l) => sum + (this.editedLotesOrden[l.receta] || l.placas_necesarias), 0);
    },
    // Unidades ya cubiertas por órdenes de producción en la cola (borrador / en proceso).
    // Si es > 0, "no hay que producir" significa "ya generaste los cortes, andá a ejecutarlos"
    // — NO "tenés stock físico". Distinguirlo evita confundir al operario.
    enColaConsolidado() {
      const cob = (this.plan && this.plan.cobertura) || [];
      return cob.reduce((s, c) => s + Number(c.en_cola || 0), 0);
    },
    enColaOrden() {
      const cob = (this.planOrden && this.planOrden.cobertura) || [];
      return cob.reduce((s, c) => s + Number(c.en_cola || 0), 0);
    },
  },
  methods: {
    ...mapActions('ordenProduccion', ['plan_consolidado', 'plan_produccion', 'generar_ordenes_plan']),
    ...mapActions('ordenPreparacionVenta', ['update_orden']),

    money(v) {
      if (v == null) return '—';
      return '$' + Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    horas(min) {
      const total = Number(parseFloat(min)) || 0;
      if (total <= 0) return '0 h';
      const h = Math.floor(total / 60);
      const m = Math.round(total % 60);
      if (h === 0) return `${m} min`;
      return m > 0 ? `${h} h ${m} min` : `${h} h`;
    },
    snack(color, text, icon) {
      this.$updateBus.$emit('showSnackBar', { show: true, button: false, color, text, icon, timeout: 3500 });
    },
    estadoColor(estado) {
      const map = { pendiente: 'blue-grey', en_preparacion: 'orange', lista: 'green', cancelada: 'red' };
      return map[estado] || 'grey';
    },

    producePara(linea, lotesMap) {
      const lotes = lotesMap[linea.receta] || linea.placas_necesarias;
      return lotes * parseFloat(linea.rinde_por_placa || 0);
    },

    n(v) {
      // Número lindo: sin decimales de relleno (10.0000 -> 10, 5.5 -> 5.5)
      return Number(v || 0);
    },

    // Pedidos que cubre un programa: junta los pedidos de todas las bandejas
    // (del pedido) que produce esta receta, tomados de la cobertura del plan.
    pedidosDeLinea(l) {
      if (!this.plan || !this.plan.cobertura) return [];
      const acc = {};
      for (const pid of (l.cubre_psps || [])) {
        const cob = this.plan.cobertura.find(c => c.psp === pid);
        for (const ped of ((cob && cob.pedidos) || [])) {
          if (!acc[ped.id]) acc[ped.id] = { ...ped, cantidad: 0 };
          acc[ped.id].cantidad += Number(ped.cantidad || 0);
        }
      }
      return Object.values(acc).sort(
        (a, b) => (b.prioridad > 0 ? 1 : 0) - (a.prioridad > 0 ? 1 : 0),
      );
    },

    // Stock final por bandeja según las placas elegidas, agregando TODOS los
    // cortes de cada programa (los del pedido y los que salen de paso).
    stockResultante(lineas, lotesMap) {
      const acc = {};
      for (const l of lineas) {
        const placas = Number(lotesMap[l.receta] ?? l.placas_necesarias) || 0;
        for (const s of (l.salidas || [])) {
          if (!acc[s.psp]) {
            acc[s.psp] = {
              psp: s.psp,
              descripcion: s.descripcion,
              stock_actual: parseFloat(s.stock_actual || 0),
              produce: 0,
              en_demanda: !!s.en_demanda,
            };
          }
          acc[s.psp].produce += placas * parseFloat(s.cantidad_por_placa || 0);
          acc[s.psp].en_demanda = acc[s.psp].en_demanda || !!s.en_demanda;
        }
      }
      return Object.values(acc)
        .map(r => ({ ...r, stock_final: r.stock_actual + r.produce }))
        .sort((a, b) => b.produce - a.produce);
    },

    async fetchPlan() {
      this.loading = true;
      try {
        const res = await this.plan_consolidado();
        this.plan = res.data;
        this.editedLotes = {};
        for (const l of res.data.lineas) {
          if (l.requiere_produccion && l.receta) {
            this.$set(this.editedLotes, l.receta, l.placas_necesarias);
          }
        }
      } catch (e) {
        this.snack('error', 'No se pudo calcular el plan', 'mdi-alert');
      } finally {
        this.loading = false;
      }
    },

    async setPrioridad(pedido, valor) {
      const nueva = parseInt(valor) || 0;
      this.$set(this.savingPrioridad, pedido.id, true);
      try {
        await this.update_orden({ id: pedido.id, data: { prioridad: nueva } });
        pedido.prioridad = nueva;
        await this.fetchPlan();
      } catch (e) {
        this.snack('error', 'No se pudo actualizar la prioridad', 'mdi-alert');
      } finally {
        this.$set(this.savingPrioridad, pedido.id, false);
      }
    },

    async generar() {
      const lineas = this.lineasAProducir.map(l => ({
        receta: l.receta,
        cantidad_lotes: this.editedLotes[l.receta] || l.placas_necesarias,
      }));
      if (!lineas.length) return;
      this.generando = true;
      try {
        const res = await this.generar_ordenes_plan(lineas);
        const n = res.data.cantidad;
        this.snack('success', `${n} orden(es) de producción generada(s)`, 'mdi-check');
        this.$router.push({ name: 'empresa.produccion.cola' });
      } catch (e) {
        this.snack('error', 'No se pudieron generar las órdenes', 'mdi-alert');
      } finally {
        this.generando = false;
      }
    },

    async seleccionarOrden(pedido) {
      this.ordenSeleccionada = pedido;
      this.planOrden = null;
      this.editedLotesOrden = {};
      this.loadingOrden = pedido.id;
      try {
        const res = await this.plan_produccion(pedido.id);
        this.planOrden = res.data;
        for (const l of res.data.lineas) {
          if (l.requiere_produccion && l.receta) {
            this.$set(this.editedLotesOrden, l.receta, l.placas_necesarias);
          }
        }
      } catch (e) {
        this.snack('error', 'No se pudo calcular el plan para este pedido', 'mdi-alert');
        this.ordenSeleccionada = null;
      } finally {
        this.loadingOrden = false;
      }
    },

    volverALista() {
      this.ordenSeleccionada = null;
      this.planOrden = null;
      this.editedLotesOrden = {};
    },

    irACola() {
      this.$router.push({ name: 'empresa.produccion.cola' });
    },

    async generarLinea(linea) {
      this.generandoLinea = linea.receta;
      try {
        const res = await this.generar_ordenes_plan([{
          receta: linea.receta,
          cantidad_lotes: this.editedLotesOrden[linea.receta] || linea.placas_necesarias,
        }]);
        this.snack('success', `Orden generada para ${linea.receta_nombre}`, 'mdi-check');
        this.$router.push({ name: 'empresa.produccion.cola' });
      } catch (e) {
        this.snack('error', 'No se pudo generar la orden', 'mdi-alert');
      } finally {
        this.generandoLinea = null;
      }
    },

    async generarParaOrden() {
      const lineas = this.lineasOrden.map(l => ({
        receta: l.receta,
        cantidad_lotes: this.editedLotesOrden[l.receta] || l.placas_necesarias,
      }));
      if (!lineas.length) return;
      this.generandoOrden = true;
      try {
        const res = await this.generar_ordenes_plan(lineas);
        const n = res.data.cantidad;
        this.snack('success', `${n} orden(es) generada(s) para ${this.ordenSeleccionada.numero_display}`, 'mdi-check');
        this.$router.push({ name: 'empresa.produccion.cola' });
      } catch (e) {
        this.snack('error', 'No se pudieron generar las órdenes', 'mdi-alert');
      } finally {
        this.generandoOrden = false;
      }
    },
  },
  created() {
    this.fetchPlan();
  },
};
</script>
